import { useState, Fragment } from 'react';
import type { ReactNode } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowsUpDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface GroupContainerProps {
  id: string;
  name: string;
  color: string;
  itemCount: number;
  isCollapsed?: boolean;
  children: ReactNode;
  onCollapse?: (collapsed: boolean) => void;
  onRename?: (newName: string) => void;
  onAddItem?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}

export function GroupContainer({
  id: _id,
  name,
  color,
  itemCount,
  isCollapsed = false,
  children,
  onCollapse,
  onRename,
  onAddItem,
  onDuplicate,
  onDelete,
}: GroupContainerProps) {
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);

  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onCollapse?.(newCollapsed);
  };

  const handleNameSubmit = () => {
    setIsEditing(false);
    if (editedName !== name && editedName.trim()) {
      onRename?.(editedName.trim());
    } else {
      setEditedName(name);
    }
  };

  return (
    <div className="mb-6">
      {/* Group header */}
      <div
        className="flex items-center gap-2 py-2 px-3 rounded-t-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        {/* Collapse toggle */}
        <button
          onClick={handleToggleCollapse}
          className="p-0.5 rounded hover:bg-white/10 transition-colors"
        >
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4" style={{ color }} />
          ) : (
            <ChevronDownIcon className="h-4 w-4" style={{ color }} />
          )}
        </button>

        {/* Color indicator */}
        <div
          className="w-1.5 h-5 rounded-full"
          style={{ backgroundColor: color }}
        />

        {/* Group name */}
        {isEditing ? (
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSubmit();
              if (e.key === 'Escape') {
                setIsEditing(false);
                setEditedName(name);
              }
            }}
            className="flex-1 font-semibold text-sm bg-transparent border-b border-gray-500 outline-none px-1"
            style={{ color }}
            autoFocus
          />
        ) : (
          <span
            className="font-semibold text-sm cursor-pointer hover:underline"
            style={{ color }}
            onClick={() => setIsEditing(true)}
          >
            {name}
          </span>
        )}

        {/* Item count */}
        <span className="text-xs text-gray-500 ml-1">
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Add item button */}
        <button
          onClick={onAddItem}
          className="p-1 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300"
          title="Add item"
        >
          <PlusIcon className="h-4 w-4" />
        </button>

        {/* Group menu */}
        <GroupOptionsMenu
          onRename={() => setIsEditing(true)}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      </div>

      {/* Group content */}
      {!collapsed && (
        <div className="border-l-2 ml-3" style={{ borderColor: color }}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Group Options Dropdown Menu
 */
function GroupOptionsMenu({
  onRename,
  onDuplicate,
  onDelete,
}: {
  onRename?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="p-1 rounded hover:bg-white/10 text-gray-500">
        <EllipsisHorizontalIcon className="h-4 w-4" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 dropdown-glass">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onRename}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <PencilIcon className="h-4 w-4 text-gray-500" />
                Rename group
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDuplicate}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                Duplicate group
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <ArrowsUpDownIcon className="h-4 w-4 text-gray-500" />
                Move to...
              </button>
            )}
          </Menu.Item>
          <div className="my-1 border-t border-white/10" />
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDelete}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-red-500/10' : ''} text-red-400`}
              >
                <TrashIcon className="h-4 w-4" />
                Delete group
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
